import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import {
  BookOpen, GraduationCap, RefreshCw, Briefcase, User, Lightbulb, Languages
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = {
  educational: { label: "Educational", icon: GraduationCap },
  personal: { label: "Personal", icon: User },
  business: { label: "Business", icon: Briefcase },
};

const EducationPage = () => {
  const { API, authHeader } = useAuth();
  const [summaries, setSummaries] = useState([]);
  const [featuredId, setFeaturedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [language, setLanguage] = useState("en");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const startedRef = useRef(false);

  const loadLibrary = async () => {
    try {
      const res = await axios.get(`${API}/trade/education`, { headers: authHeader() });
      setSummaries(res.data);
      setFeaturedId(prev => prev || res.data[0]?.id || null);
      return res.data;
    } catch {
      toast.error("Could not load book summaries");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const generateSummary = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/trade/education/generate`, {}, { headers: authHeader(), timeout: 240000 });
      setSummaries(prev => [res.data, ...prev]);
      setFeaturedId(res.data.id);
      toast.success("New book summary generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate a new summary");
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    loadLibrary().then(generateSummary);
  }, []);

  const featured = summaries.find(s => s.id === featuredId) || summaries[0];
  const filtered = summaries.filter(s => categoryFilter === "all" || s.category === categoryFilter);
  const summaryText = featured ? (language === "am" ? featured.summary_am : featured.summary_en) : "";
  const ideas = featured ? (language === "am" ? featured.key_ideas_am : featured.key_ideas_en) : [];
  const CategoryIcon = featured ? CATEGORIES[featured.category]?.icon || BookOpen : BookOpen;

  return (
    <div className="trade-section pt-32 lg:pt-20 pb-20 min-h-screen">
      <div className="lg:ml-64 px-6 md:px-12 py-10 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start justify-between flex-wrap gap-4 mb-10"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Education</p>
            <h1 className="text-4xl md:text-5xl font-serif font-bold">Book Summaries</h1>
            <p className="text-white/50 mt-2">A fresh educational, personal or business book with 10 key ideas every time you open this page</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="flex rounded-sm overflow-hidden border border-white/10" data-testid="education-language-toggle">
              <button
                onClick={() => setLanguage("en")}
                className={`px-4 py-2 text-xs ${language === "en" ? "bg-[#D4AF37] text-black font-semibold" : "bg-white/5 text-white/60"}`}
                data-testid="education-lang-en"
              >
                English
              </button>
              <button
                onClick={() => setLanguage("am")}
                className={`px-4 py-2 text-xs ${language === "am" ? "bg-[#D4AF37] text-black font-semibold" : "bg-white/5 text-white/60"}`}
                data-testid="education-lang-am"
              >
                አማርኛ
              </button>
            </div>
            <Button
              onClick={generateSummary}
              disabled={generating}
              className="trade-btn-primary rounded-sm h-10 px-5"
              data-testid="generate-book-btn"
            >
              <RefreshCw size={15} className={`mr-2 ${generating ? "animate-spin" : ""}`} />
              {generating ? "Generating..." : "New Book"}
            </Button>
          </div>
        </motion.div>

        {generating && (
          <div className="trade-card rounded-sm p-4 mb-6 flex items-center gap-3 text-sm text-white/65" data-testid="education-generating">
            <RefreshCw size={16} className="trade-gold animate-spin" />
            ChatGPT is preparing a new bilingual book summary. This can take about one minute.
          </div>
        )}

        {loading ? (
          <div className="trade-card rounded-sm p-16 text-center text-white/40">Loading Education...</div>
        ) : !featured ? (
          <div className="trade-card rounded-sm p-16 text-center" data-testid="education-empty">
            <BookOpen className="w-16 h-16 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
            <h3 className="text-xl font-serif mb-2">No book summaries yet</h3>
            <p className="text-white/50">The first summary is being generated for this page.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* Featured summary */}
            <motion.article
              key={featured.id + language}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="xl:col-span-2 trade-card rounded-sm p-6 md:p-8"
              data-testid="featured-book-card"
            >
              <div className="flex items-start justify-between gap-4 mb-6">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <CategoryIcon className="w-6 h-6 trade-gold" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.25em] trade-gold mb-1">
                      {CATEGORIES[featured.category]?.label || featured.category}
                    </p>
                    <h2 className="text-2xl md:text-3xl font-serif font-bold" data-testid="featured-book-title">{featured.title}</h2>
                    <p className="text-white/50 text-sm mt-1" data-testid="featured-book-author">{featured.author}</p>
                  </div>
                </div>
                <Languages className="w-5 h-5 text-white/25 flex-shrink-0" />
              </div>

              <p className="text-white/70 leading-relaxed mb-8 whitespace-pre-wrap" data-testid="featured-book-summary">
                {summaryText}
              </p>

              <div>
                <h3 className="text-sm uppercase tracking-widest trade-gold mb-4">
                  {language === "am" ? "10 ቁልፍ ሀሳቦች" : "10 Key Ideas"}
                </h3>
                <div className="space-y-3" data-testid="featured-key-ideas">
                  {ideas.map((idea, i) => (
                    <motion.div
                      key={`${featured.id}-${language}-${i}`}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className="flex gap-3 p-3 bg-white/5 rounded-sm"
                      data-testid={`key-idea-${i + 1}`}
                    >
                      <span className="w-7 h-7 rounded-sm bg-[#D4AF37]/10 trade-gold flex items-center justify-center text-xs font-semibold flex-shrink-0">
                        {i + 1}
                      </span>
                      <p className="text-sm text-white/75 leading-relaxed">{idea}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.article>

            {/* Library */}
            <aside className="xl:col-span-1">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm uppercase tracking-widest trade-gold">Library</h2>
                <span className="text-xs text-white/40">{summaries.length} books</span>
              </div>
              <div className="flex gap-2 mb-4 overflow-x-auto" data-testid="education-category-filter">
                {[
                  { value: "all", label: "All" },
                  { value: "educational", label: "Educational" },
                  { value: "personal", label: "Personal" },
                  { value: "business", label: "Business" },
                ].map(c => (
                  <button
                    key={c.value}
                    onClick={() => setCategoryFilter(c.value)}
                    className={`px-3 py-2 text-xs rounded-sm whitespace-nowrap ${
                      categoryFilter === c.value ? "bg-[#D4AF37] text-black font-semibold" : "bg-white/5 text-white/60 hover:text-white"
                    }`}
                    data-testid={`education-filter-${c.value}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                {filtered.map(book => {
                  const Icon = CATEGORIES[book.category]?.icon || Lightbulb;
                  const active = featured?.id === book.id;
                  return (
                    <button
                      key={book.id}
                      onClick={() => setFeaturedId(book.id)}
                      className={`w-full text-left trade-card rounded-sm p-4 transition-all ${
                        active ? "border-[#D4AF37]/50 bg-[#D4AF37]/5" : ""
                      }`}
                      data-testid={`library-book-${book.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 bg-white/5 rounded-sm flex items-center justify-center flex-shrink-0">
                          <Icon size={16} className="trade-gold" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm truncate">{book.title}</p>
                          <p className="text-[11px] text-white/40 truncate">{book.author}</p>
                          <p className="text-[10px] uppercase tracking-widest text-white/30 mt-2">
                            {CATEGORIES[book.category]?.label || book.category}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

export default EducationPage;
